/**
 * Decides when a vehicle has genuinely started moving or genuinely stopped.
 *
 * Kept free of React so the rules can be tested directly — the flapping this
 * guards against only shows up over time, which is miserable to reproduce by
 * hand in a browser.
 */

/**
 * Movement thresholds with a deliberate gap between them.
 *
 * A single threshold makes a car crawling in traffic flip between states every
 * few seconds, and every flip would be a notification. The gap means a vehicle
 * has to really get going, or really stop, to change state.
 */
export const MOVING_ABOVE_KPH = 8;
export const STOPPED_BELOW_KPH = 3;

/** A vehicle must hold the new state this long before it counts. */
export const SETTLE_MS = 45_000;

/** And this is the floor between two alerts about the same vehicle. */
export const COOLDOWN_MS = 90_000;

export type AlertKind = 'started' | 'stopped';
export type Phase = 'moving' | 'stopped';

export interface VehicleAlert {
  deviceId: number;
  name: string;
  kind: AlertKind;
  at: number;
}

/** Just the parts of a device this needs, so tests do not build whole fixtures. */
export interface MovementInput {
  id: number;
  name: string;
  online: boolean;
  speedKph: number | undefined;
}

interface Watch {
  /** The state the customer has already been told about. */
  announced: Phase;
  /** The state it currently looks like, and since when. */
  candidate: Phase;
  since: number;
  /** Null until the first alert: the cooldown separates alerts, it does not delay the first. */
  lastAlertAt: number | null;
}

function phaseOf(device: MovementInput, previous: Phase | undefined): Phase | undefined {
  if (device.speedKph === undefined || !device.online) return previous;
  if (device.speedKph >= MOVING_ABOVE_KPH) return 'moving';
  if (device.speedKph <= STOPPED_BELOW_KPH) return 'stopped';
  // Inside the gap: whatever it was, it still is.
  return previous;
}

export class MovementWatcher {
  private readonly watches = new Map<number, Watch>();
  /** The first batch establishes a baseline and announces nothing. */
  private primed = false;

  /**
   * Feed in the current fleet; get back whatever just changed.
   *
   * Nothing fires from the first call: on opening the dashboard every vehicle
   * would otherwise announce itself at once, which is noise, not information.
   */
  push(devices: MovementInput[], now: number): VehicleAlert[] {
    const alerts: VehicleAlert[] = [];

    for (const device of devices) {
      const existing = this.watches.get(device.id);
      const phase = phaseOf(device, existing?.candidate);
      if (phase === undefined) continue;

      if (!existing) {
        this.watches.set(device.id, {
          announced: phase,
          candidate: phase,
          since: now,
          lastAlertAt: null,
        });
        continue;
      }

      if (phase !== existing.candidate) {
        existing.candidate = phase;
        existing.since = now;
        continue;
      }

      const settled = now - existing.since >= SETTLE_MS;
      const cooledDown =
        existing.lastAlertAt === null || now - existing.lastAlertAt >= COOLDOWN_MS;

      if (phase !== existing.announced && settled && cooledDown) {
        existing.announced = phase;
        existing.lastAlertAt = now;
        if (this.primed) {
          alerts.push({
            deviceId: device.id,
            name: device.name,
            kind: phase === 'moving' ? 'started' : 'stopped',
            at: now,
          });
        }
      }
    }

    this.primed = true;

    // Stop tracking vehicles the customer no longer has access to.
    const live = new Set(devices.map((d) => d.id));
    for (const id of this.watches.keys()) {
      if (!live.has(id)) this.watches.delete(id);
    }

    return alerts;
  }

  /** Exposed for tests and for the "forget everything" path on sign-out. */
  reset(): void {
    this.watches.clear();
    this.primed = false;
  }
}
